import {
  AudioOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  FileOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useAddAlert } from '@/hooks/useAddAlert'
import cn from 'classnames'
import { ChangeEvent, PropsWithChildren, useId, useMemo } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { isCrmIntegrationOutgoingAttachmentsEnabled } from '../../lib/crmChatProviderKey'
import {
  getCrmIntegrationOutgoingAttachmentsHint,
  getCrmIntegrationOutgoingFileAccept,
  isCrmIntegrationOutgoingVoiceRecorderAllowed,
  validateCrmIntegrationOutgoingAttachment,
} from '../../lib/crmIntegrationOutgoingAttachment'
import {
  getWazzupOutgoingAttachmentsHint,
  getWazzupOutgoingFileAccept,
  isWazzupOutgoingVoiceRecorderAllowed,
  validateWazzupOutgoingAttachment,
} from '../../lib/crmIntegrationWazzupOutgoingAttachment'
import classes from '../../styles/crmIntegrationComposer.module.css'

export type CrmIntegrationTextChatComposerProps = PropsWithChildren<{
  compact?: boolean
  disabled?: boolean
  provider: CrmChatProviderKey
  /** Wazzup: external_chat_type / channel.type текущего диалога. */
  wazzupChannelType?: string | null
  attachmentFile?: File | null
  onAttachmentFileChange?: (file: File | null) => void
  voiceMode?: boolean
  onOpenVoiceMode?: () => void
  onCloseVoiceMode?: () => void
}>

const CrmIntegrationTextChatComposer = ({
  compact = false,
  disabled = false,
  provider,
  wazzupChannelType = null,
  attachmentFile = null,
  onAttachmentFileChange,
  voiceMode = false,
  onOpenVoiceMode,
  onCloseVoiceMode,
  children,
}: CrmIntegrationTextChatComposerProps) => {
  const addAlert = useAddAlert()
  const fileInputId = useId()
  /** Wazzup: строго 1 файл; остальные провайдеры в UI тоже по одному за сообщение. */
  const isAttachDisabled = disabled || Boolean(attachmentFile)
  const outgoingAttachmentsEnabled =
    isCrmIntegrationOutgoingAttachmentsEnabled(provider)
  const voiceRecorderAllowed =
    provider === 'wazzup'
      ? isWazzupOutgoingVoiceRecorderAllowed(wazzupChannelType)
      : isCrmIntegrationOutgoingVoiceRecorderAllowed(provider)

  const fileAccept = useMemo(() => {
    if (provider === 'wazzup') {
      return getWazzupOutgoingFileAccept(wazzupChannelType)
    }
    return getCrmIntegrationOutgoingFileAccept(provider)
  }, [provider, wazzupChannelType])

  const attachmentsHint = useMemo(() => {
    if (provider === 'wazzup') {
      return getWazzupOutgoingAttachmentsHint(wazzupChannelType)
    }
    return getCrmIntegrationOutgoingAttachmentsHint(provider)
  }, [provider, wazzupChannelType])

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (!outgoingAttachmentsEnabled) {
      return
    }

    // FileList «живой»: после сброса value он очищается — копируем до reset.
    const selected = event.target.files
      ? Array.from(event.target.files)
      : []
    event.target.value = ''

    if (!selected.length) {
      return
    }

    // Wazzup и текущий composer: только 1 файл; лишние отсекаем до отправки.
    if (selected.length > 1) {
      addAlert({
        type: 'error',
        title: 'Слишком много файлов',
        text:
          provider === 'wazzup'
            ? 'В Wazzup можно отправить только 1 файл за сообщение'
            : 'Сейчас можно прикрепить только 1 файл за сообщение',
      })
    }

    const file = selected[0]
    if (!file) {
      return
    }

    if (provider === 'wazzup') {
      const wazzupError = validateWazzupOutgoingAttachment(
        file,
        wazzupChannelType,
      )
      if (wazzupError) {
        addAlert({
          type: 'error',
          title: 'Нельзя отправить файл',
          text: wazzupError,
        })
        return
      }
      onAttachmentFileChange?.(file)
      return
    }

    const validationError = validateCrmIntegrationOutgoingAttachment(
      file,
      provider,
    )
    if (validationError) {
      addAlert({
        type: 'error',
        title: 'Нельзя отправить файл',
        text: validationError,
      })
      return
    }

    onAttachmentFileChange?.(file)
  }

  // MAX / ЦИАН(Wazzup soft): пустой accept = любой файл.
  const allowEmptyAccept =
    provider === 'max' || (provider === 'wazzup' && !fileAccept)
  const isFileInputDisabled =
    isAttachDisabled || (!fileAccept && !allowEmptyAccept)

  return (
    <footer
      className={cn(classes.composer, {
        [classes.composerCompact]: compact,
      })}
    >
      {outgoingAttachmentsEnabled && attachmentFile ? (
        <div className={classes.composerAttachments}>
          <ul className={classes.composerAttachmentsList}>
            <li className={classes.composerAttachmentItem}>
              <div className={classes.composerAttachmentCard}>
                <button
                  type="button"
                  className={classes.composerAttachmentRemove}
                  aria-label={`Убрать файл ${attachmentFile.name}`}
                  disabled={disabled}
                  onClick={() => onAttachmentFileChange?.(null)}
                >
                  <CloseCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
                <FileOutlined
                  className={classes.composerAttachmentIcon}
                  aria-hidden
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <span
                  className={classes.composerAttachmentName}
                  title={attachmentFile.name}
                >
                  {attachmentFile.name}
                </span>
              </div>
            </li>
          </ul>
        </div>
      ) : null}

      <div className={classes.composerMainRow}>
        {voiceMode && outgoingAttachmentsEnabled ? (
          <>
            <button
              type="button"
              className={classes.attachButton}
              aria-label="Отменить запись голосового сообщения"
              onClick={() => onCloseVoiceMode?.()}
            >
              <CloseOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
            {children}
          </>
        ) : (
          <>
            {outgoingAttachmentsEnabled ? (
              <div className={classes.composerActions}>
                <input
                  id={fileInputId}
                  type="file"
                  accept={fileAccept || undefined}
                  className={classes.composerFileInput}
                  disabled={isFileInputDisabled}
                  tabIndex={-1}
                  onChange={handleFilesSelected}
                />
                <label
                  htmlFor={fileInputId}
                  className={cn(classes.attachButton, {
                    [classes.attachButtonDisabled]: isFileInputDisabled,
                  })}
                  aria-label="Прикрепить файл"
                  aria-disabled={isFileInputDisabled}
                >
                  <PaperClipOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </label>
                <TooltipWrapper
                  title={attachmentsHint}
                  placement="topLeft"
                  overlayClassName={classes.formatsTooltipOverlay}
                >
                  <span className={classes.infoTooltipTrigger}>
                    <button
                      type="button"
                      className={cn(classes.infoButton, {
                        [classes.infoButtonDisabled]: disabled,
                      })}
                      aria-label="Какие файлы можно отправить"
                      aria-disabled={disabled}
                      tabIndex={disabled ? -1 : 0}
                    >
                      <InfoCircleOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </button>
                  </span>
                </TooltipWrapper>
                {voiceRecorderAllowed ? (
                  <button
                    type="button"
                    className={cn(classes.attachButton, {
                      [classes.attachButtonDisabled]: disabled,
                    })}
                    aria-label="Голосовое сообщение"
                    aria-disabled={disabled}
                    disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => onOpenVoiceMode?.()}
                  >
                    <AudioOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                ) : null}
              </div>
            ) : null}
            {children}
          </>
        )}
      </div>
    </footer>
  )
}

export default CrmIntegrationTextChatComposer
